import { useEffect, useRef, useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getServices } from '@/screens/services/NewServices';
import { IoIosArrowDown } from 'react-icons/io';
import { useTranslation } from 'react-i18next';

// Helper function to format text with backticks as italics
const formatTextWithItalics = (text: string): ReactNode => {
  // Check if text contains backticks
  if (!text.includes('`')) return text;
  
  // Split the text by backticks
  const parts = text.split('`');
  const result: ReactNode[] = [];
  
  // Alternate between normal and italic text
  parts.forEach((part, index) => {
    if (index % 2 === 0) {
      // Even indices are regular text
      result.push(part);
    } else {
      // Odd indices should be italic
      result.push(<i key={index}>{part}</i>);
    }
  });
  
  return result;
};

// Define color mapping for each service
const serviceColors: Record<string, string> = {
    'car-polish': 'text-trhine-red-100',
    'aircond': 'text-trhine-blue-100',
    'fabric': 'text-trhine-yellow-100',
    'sanitise': 'text-trhine-green-100',
    'pest-control': 'text-trhine-brown-100',
};

const ServicesDropdown = () => {
    const { t } = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Get translated services
    const services = getServices(t);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Determine the active service and get its color
    const activeService = services.find(service => location.pathname.startsWith(`/services/${service.id}`));
    const activeColor = activeService ? serviceColors[activeService.id] || 'text-black' : 'text-black';

    return (
        <div ref={dropdownRef} className="relative">
            {/* Desktop: Dropdown button */}
            <button
                className={`text-md hover:text-gray-300 transition-colors flex items-center gap-1 ${activeColor} md:inline-flex hidden`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                {t('navbar.services')}
                <IoIosArrowDown
                    className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Mobile: Menu item */}
            <div className="md:hidden w-full">
                <button
                    className="text-md flex items-center gap-1 w-full justify-between px-0"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    {t('navbar.services')}
                    <IoIosArrowDown
                        className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                    />
                </button>
                
                {/* Mobile: Dropdown items */}
                {isDropdownOpen && (
                    <div className="pl-4 mt-2 space-y-2">
                        {services.map((service) => {
                            const isActive = location.pathname.startsWith(`/services/${service.id}`);
                            const textColor = serviceColors[service.id] || 'text-gray-700';

                            return (
                                <Link
                                    key={service.id}
                                    to={`/services/${service.id}`}
                                    className={`block text-sm transition-colors ${
                                        isActive ? `${textColor} font-bold` : 'text-gray-600'
                                    }`}
                                    onClick={() => setIsDropdownOpen(false)}
                                >
                                    {formatTextWithItalics(service.title)}
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Desktop: Dropdown menu */}
            <div className={`
                absolute 
                hidden
                md:block
                left-1/2 
                -translate-x-1/2 
                mt-2 
                w-48 
                bg-white 
                rounded-md 
                shadow-lg 
                py-1 
                transition-all 
                duration-300
                ${isDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
                ${location.pathname.includes('/services') ? 'top-full' : ''}
            `}>
                {services.map((service) => {
                    const isActive = location.pathname.startsWith(`/services/${service.id}`);
                    const textColor = serviceColors[service.id] || 'text-gray-700';

                    return (
                        <Link
                            key={service.id}
                            to={`/services/${service.id}`}
                            className={`block px-4 py-2 text-sm transition-colors ${
                                isActive ? `${textColor} font-bold` : `hover:bg-gray-100`
                            }`}
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            {formatTextWithItalics(service.title)}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}

export default ServicesDropdown;
